import React, { useEffect, useState } from 'react';
import { Download, MoreHorizontal } from 'lucide-react';
import type { ContractorAgreement, ContractorApplicant, ContractorApplicantFile } from '../../../types/contractorOnboard';
import type { CrewJob, CrewJobCommit } from '../../../types/crewJobs';
import {
  contractorFullName,
  canGrandfatherContractorIntoPool,
  crewOnboardPublicUrl,
  deriveContractorPipeline,
} from '../../../utils/contractorOnboard';
import {
  getContractorOnboardFileUrl,
  grandfatherContractorIntoPool,
  requestContractorForms,
  reviewContractorPacket,
  subscribeLatestContractorAgreement,
} from '../../../utils/contractorOnboardFirestore';
import { formatContractorPay } from '../../../utils/contractorAgreement';
import { payBandFromJob } from '../../../utils/crewJobsPay';
import {
  buildContractorOnboardDocumentId,
  contractorOnboardViewerKind,
  type ContractorOnboardPacketKind,
  type ContractorOnboardViewerOpen,
} from '../../../utils/contractorOnboardViewer';
import ContractorAgreementPane from './ContractorAgreementPane';
import ContractorAgreementPdfButtons from './ContractorAgreementPdfButtons';
import ContractorPipeline from './ContractorPipeline';

type Props = {
  applicant: ContractorApplicant;
  job: CrewJob | null;
  commit?: CrewJobCommit | null;
  reviewerUid: string;
  onOpenPacketDoc?: (doc: ContractorOnboardViewerOpen) => void;
  onConfirm?: () => void;
  onDecline?: () => void;
  onRestore?: () => void;
  onDelete?: () => void;
};

const ContractorOnboardPanel: React.FC<Props> = ({
  applicant,
  job,
  commit,
  reviewerUid,
  onOpenPacketDoc,
  onConfirm,
  onDecline,
  onRestore,
  onDelete,
}) => {
  const [agreement, setAgreement] = useState<ContractorAgreement | null>(null);
  const [w9Url, setW9Url] = useState<string | null>(null);
  const [idUrl, setIdUrl] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [viewBusy, setViewBusy] = useState<ContractorOnboardPacketKind | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [sentNote, setSentNote] = useState<string | null>(null);
  const [w9Ok, setW9Ok] = useState(Boolean(applicant.w9ApprovedAt));
  const [idOk, setIdOk] = useState(Boolean(applicant.photoIdApprovedAt));
  const [grandfatherOpen, setGrandfatherOpen] = useState(false);
  const [gfW9, setGfW9] = useState(false);
  const [gfId, setGfId] = useState(false);
  const [gfAgreement, setGfAgreement] = useState(false);
  const [gfNote, setGfNote] = useState('');
  const [gfPayAmount, setGfPayAmount] = useState('');
  const [gfPayUnit, setGfPayUnit] = useState<'hour' | 'day'>('day');
  const steps = deriveContractorPipeline(applicant);
  const canRequestForms = applicant.status === 'submitted' || applicant.status === 'forms_requested';
  const canGrandfather = canGrandfatherContractorIntoPool(applicant);
  const fullName = contractorFullName(applicant);
  const commitBlocked = commit?.status === 'declined' || commit?.status === 'withdrawn';
  const trailBlocked = applicant.status === 'declined';
  const isBlocked = commitBlocked || trailBlocked;

  useEffect(() => {
    setAgreement(null);
    return subscribeLatestContractorAgreement(applicant.id, setAgreement);
  }, [applicant.id]);

  useEffect(() => {
    setW9Ok(Boolean(applicant.w9ApprovedAt));
    setIdOk(Boolean(applicant.photoIdApprovedAt));
    setSentNote(null);
    setW9Url(null);
    setIdUrl(null);
    setViewBusy(null);
    setGrandfatherOpen(false);
    setGfW9(false);
    setGfId(false);
    setGfAgreement(false);
    setGfNote('');
    const band = job ? payBandFromJob(job) : null;
    if (band?.min) {
      setGfPayAmount(String(band.min));
      setGfPayUnit(band.unit === 'hour' ? 'hour' : 'day');
    } else {
      setGfPayAmount('');
      setGfPayUnit('day');
    }
  }, [applicant.id, applicant.w9ApprovedAt, applicant.photoIdApprovedAt, job?.id]);

  // Resolve packet URLs only when staff opens a viewer (pooled summary stays condensed).
  const ensurePacketUrl = async (
    kind: ContractorOnboardPacketKind,
    file: ContractorApplicantFile,
  ): Promise<string> => {
    const cached = kind === 'w9' ? w9Url : idUrl;
    if (cached) return cached;
    const url = await getContractorOnboardFileUrl(file.storagePath);
    if (kind === 'w9') setW9Url(url);
    else setIdUrl(url);
    return url;
  };

  const requestForms = async () => {
    setBusy(true);
    setError(null);
    setSentNote(null);
    try {
      await requestContractorForms(applicant.id);
      setSentNote(`Email sent to ${applicant.email}`);
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  };

  const approvePacket = async () => {
    setBusy(true);
    setError(null);
    try {
      await reviewContractorPacket(applicant.id, {
        w9Approved: w9Ok,
        photoIdApproved: idOk,
        reviewerUid,
      });
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  };

  const runGrandfather = async () => {
    setBusy(true);
    setError(null);
    try {
      await grandfatherContractorIntoPool({
        applicantId: applicant.id,
        w9OnFile: gfW9,
        photoIdOnFile: gfId,
        agreementSigned: gfAgreement,
        note: gfNote.trim() || undefined,
        payAmount: gfPayAmount.trim() ? Number(gfPayAmount) : undefined,
        payUnit: gfPayUnit,
        jobTitle: job?.title || applicant.lastJobTitle || undefined,
      });
      setSentNote(`${fullName} is in the 1099 pool`);
      setGrandfatherOpen(false);
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  };

  const openPacketDoc = async (kind: ContractorOnboardPacketKind) => {
    if (!onOpenPacketDoc) return;
    const file = kind === 'w9' ? applicant.w9 : applicant.photoId;
    if (!file?.storagePath) return;
    const viewerKind = contractorOnboardViewerKind(file);
    if (!viewerKind) {
      setError(kind === 'w9' ? 'W-9 must be a PDF or image to open in the viewer.' : 'Photo ID must be a PDF or image to open in the viewer.');
      return;
    }
    setError(null);
    setViewBusy(kind);
    try {
      const url = await ensurePacketUrl(kind, file);
      const label = kind === 'w9' ? 'W-9' : 'Photo ID';
      onOpenPacketDoc({
        url,
        title: `${fullName} — ${label}`,
        documentId: buildContractorOnboardDocumentId(applicant.id, kind),
        kind: viewerKind,
        mimeType: file.contentType || undefined,
        leadName: fullName || undefined,
      });
    } catch (e) {
      setError((e as Error).message || `Could not open ${kind === 'w9' ? 'W-9' : 'photo ID'}`);
    } finally {
      setViewBusy(null);
    }
  };

  const downloadPacketDoc = async (kind: ContractorOnboardPacketKind) => {
    const file = kind === 'w9' ? applicant.w9 : applicant.photoId;
    if (!file?.storagePath) return;
    setError(null);
    try {
      const url = await ensurePacketUrl(kind, file);
      const link = document.createElement('a');
      link.href = url;
      link.target = '_blank';
      link.rel = 'noopener noreferrer';
      link.download = file.fileName || (kind === 'w9' ? 'w9.pdf' : 'photo-id');
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (e) {
      setError((e as Error).message || `Could not download ${kind === 'w9' ? 'W-9' : 'photo ID'}`);
    }
  };

  const renderPacketFile = (
    kind: ContractorOnboardPacketKind,
    file: ContractorApplicantFile | null | undefined,
    emptyLabel: string,
  ) => {
    const label = kind === 'w9' ? 'W-9' : 'Photo ID';
    if (!file) {
      return (
        <div className="crew-packet-row crew-packet-row--empty">
          <span className="crew-packet-label">{label}</span>
          <span className="people-hiring-muted">{emptyLabel}</span>
        </div>
      );
    }
    const viewerKind = contractorOnboardViewerKind(file);
    return (
      <div className="crew-packet-row">
        <div className="crew-packet-meta">
          <span className="crew-packet-label">{label}</span>
          <span className="people-hiring-muted crew-packet-file" title={file.fileName}>{file.fileName}</span>
        </div>
        <div className="crew-packet-actions">
          {viewerKind && onOpenPacketDoc && (
            <button
              type="button"
              className="pm-btn pm-btn-ghost"
              disabled={viewBusy !== null || busy}
              onClick={() => void openPacketDoc(kind)}
            >
              {viewBusy === kind ? 'Opening…' : 'View'}
            </button>
          )}
          <button
            type="button"
            className="pm-btn pm-btn-ghost"
            disabled={viewBusy !== null || busy}
            onClick={() => void downloadPacketDoc(kind)}
            aria-label={`Download ${label}`}
          >
            <Download size={14} />
          </button>
        </div>
      </div>
    );
  };

  if (isBlocked) {
    return (
      <div className="crew-candidate-expand">
        <div className="crew-candidate-blocked">
          <div>
            <strong>{commitBlocked ? 'Off this raise' : 'Onboarding declined'}</strong>
            <p className="people-hiring-muted">
              {commitBlocked
                ? `${fullName} was ${commit?.status === 'withdrawn' ? 'withdrawn' : 'declined'} for this raise. Restore to continue onboarding, or delete them.`
                : `${fullName}'s 1099 trail is declined. Restore to pick up where they left off, or delete them.`}
            </p>
          </div>
          <div className="crew-candidate-blocked-actions">
            {onRestore && (
              <button type="button" className="ops-new-btn" disabled={busy} onClick={onRestore}>
                Restore
              </button>
            )}
            {onDelete && (
              <button type="button" className="pm-btn pm-btn-ghost" disabled={busy} onClick={onDelete}>
                Delete
              </button>
            )}
          </div>
        </div>
        {error && <p className="people-hiring-resume-error">{error}</p>}
      </div>
    );
  }

  const moreMenu = (
    <details className="crew-candidate-more">
      <summary aria-label="More actions">
        <MoreHorizontal size={16} />
      </summary>
      <div className="crew-candidate-more-menu" role="menu">
        {applicant.publicSlug && applicant.status !== 'submitted' && (
          <a
            href={crewOnboardPublicUrl(applicant.publicSlug)}
            target="_blank"
            rel="noreferrer"
            onClick={e => e.stopPropagation()}
          >
            Open public link
          </a>
        )}
        {commit && commit.status !== 'confirmed' && onConfirm && (
          <button
            type="button"
            disabled={applicant.status !== 'pooled'}
            title={applicant.status === 'pooled' ? undefined : 'Finish 1099 onboarding first'}
            onClick={onConfirm}
          >
            Confirm to Roster
          </button>
        )}
        {canGrandfather && (
          <button
            type="button"
            disabled={busy}
            onClick={() => setGrandfatherOpen(open => !open)}
          >
            {grandfatherOpen ? 'Hide grandfather' : 'Grandfather into pool'}
          </button>
        )}
        {commit && commit.status !== 'declined' && onDecline && (
          <button type="button" onClick={onDecline}>
            Decline for raise
          </button>
        )}
        {onDelete && (
          <button type="button" className="is-danger" onClick={onDelete}>
            Delete
          </button>
        )}
      </div>
    </details>
  );

  // Pooled: condensed summary only — never flash the draft agreement editor while loading.
  if (applicant.status === 'pooled') {
    const payLabel = agreement?.fields
      ? formatContractorPay(agreement.fields)
      : null;
    return (
      <div className="crew-candidate-expand">
        <div className="crew-candidate-toolbar">
          <ContractorPipeline steps={steps} />
          <div className="crew-candidate-toolbar-actions">{moreMenu}</div>
        </div>
        <div className="crew-pooled-summary">
          <div className="crew-pooled-summary-row">
            <span className="crew-pooled-summary-label">Packet</span>
            <span className="crew-pooled-summary-value">
              {applicant.w9 ? 'W-9 on file' : 'W-9 missing'}
              {' · '}
              {applicant.photoId ? 'Photo ID on file' : 'Photo ID missing'}
            </span>
            <span className="crew-pooled-summary-actions">
              {applicant.w9 && onOpenPacketDoc && (
                <button
                  type="button"
                  className="pm-btn pm-btn-ghost"
                  disabled={viewBusy !== null}
                  onClick={() => void openPacketDoc('w9')}
                >
                  {viewBusy === 'w9' ? 'Opening…' : 'View W-9'}
                </button>
              )}
              {applicant.photoId && onOpenPacketDoc && (
                <button
                  type="button"
                  className="pm-btn pm-btn-ghost"
                  disabled={viewBusy !== null}
                  onClick={() => void openPacketDoc('id')}
                >
                  {viewBusy === 'id' ? 'Opening…' : 'View ID'}
                </button>
              )}
            </span>
          </div>
          <div className="crew-pooled-summary-row">
            <span className="crew-pooled-summary-label">Agreement</span>
            <span className="crew-pooled-summary-value">
              {agreement
                ? `Signed${payLabel ? ` — ${payLabel}` : ''}`
                : 'Signed · loading details…'}
            </span>
            {agreement && (
              <ContractorAgreementPdfButtons
                applicant={applicant}
                agreement={agreement}
                onOpenPacketDoc={onOpenPacketDoc}
              />
            )}
          </div>
        </div>
        {error && <p className="people-hiring-resume-error">{error}</p>}
      </div>
    );
  }

  return (
    <div className="crew-candidate-expand">
      <div className="crew-candidate-toolbar">
        <ContractorPipeline steps={steps} />
        <div className="crew-candidate-toolbar-actions">
          {canRequestForms && (
            <button type="button" className="ops-new-btn" disabled={busy} onClick={() => void requestForms()}>
              {busy ? 'Sending…' : applicant.status === 'forms_requested' ? 'Resend forms' : 'Request forms'}
            </button>
          )}
          {moreMenu}
        </div>
      </div>

      {sentNote && <p className="people-hiring-muted crew-candidate-note">{sentNote}</p>}
      {error && <p className="people-hiring-resume-error">{error}</p>}

      {grandfatherOpen && canGrandfather && (
        <div className="crew-grandfather">
          <strong>Grandfather into pool</strong>
          <p className="people-hiring-muted">
            Use when W-9, photo ID, and a signed agreement are already on file outside this trail.
          </p>
          <label>
            <input type="checkbox" checked={gfW9} onChange={e => setGfW9(e.target.checked)} /> W-9 on file
          </label>
          <label>
            <input type="checkbox" checked={gfId} onChange={e => setGfId(e.target.checked)} /> Photo ID on file
          </label>
          <label>
            <input type="checkbox" checked={gfAgreement} onChange={e => setGfAgreement(e.target.checked)} /> Agreement signed
          </label>
          <div className="people-hiring-field" style={{ marginTop: 8 }}>
            <label htmlFor={`gf-pay-${applicant.id}`}>Pay rate (optional)</label>
            <div className="people-hiring-resume-actions">
              <input
                id={`gf-pay-${applicant.id}`}
                type="number"
                min={0}
                step="0.01"
                value={gfPayAmount}
                onChange={e => setGfPayAmount(e.target.value)}
                placeholder="Amount"
                style={{ maxWidth: 120 }}
              />
              <select value={gfPayUnit} onChange={e => setGfPayUnit(e.target.value === 'hour' ? 'hour' : 'day')}>
                <option value="day">per day</option>
                <option value="hour">per hour</option>
              </select>
            </div>
          </div>
          <div className="people-hiring-field" style={{ marginTop: 8 }}>
            <label htmlFor={`gf-note-${applicant.id}`}>Staff note (optional)</label>
            <input
              id={`gf-note-${applicant.id}`}
              type="text"
              value={gfNote}
              onChange={e => setGfNote(e.target.value)}
              placeholder="e.g. Signed paper agreement Sept 2025"
            />
          </div>
          <div className="people-hiring-resume-actions" style={{ marginTop: 10 }}>
            <button
              type="button"
              className="ops-new-btn"
              disabled={busy || !gfW9 || !gfId || !gfAgreement}
              onClick={() => void runGrandfather()}
            >
              {busy ? 'Saving…' : 'Confirm into pool'}
            </button>
          </div>
        </div>
      )}

      {(applicant.status === 'forms_requested' || applicant.status === 'forms_approved' || applicant.w9 || applicant.photoId) && (
        <div className="crew-packet">
          <div className="crew-packet-head">
            <strong>Packet</strong>
          </div>
          {renderPacketFile('w9', applicant.w9, 'Not uploaded')}
          {renderPacketFile('id', applicant.photoId, 'Not uploaded')}
          {applicant.status === 'forms_requested' && applicant.w9 && applicant.photoId && (
            <div className="crew-packet-approve">
              <label>
                <input type="checkbox" checked={w9Ok} onChange={e => setW9Ok(e.target.checked)} /> W-9 OK
              </label>
              <label>
                <input type="checkbox" checked={idOk} onChange={e => setIdOk(e.target.checked)} /> Photo ID OK
              </label>
              <button
                type="button"
                className="ops-new-btn"
                disabled={busy || !w9Ok || !idOk}
                onClick={() => void approvePacket()}
              >
                {busy ? 'Saving…' : 'Approve packet'}
              </button>
            </div>
          )}
        </div>
      )}

      {(applicant.status === 'forms_approved' || applicant.status === 'agreement_sent') && (
        <ContractorAgreementPane
          applicant={applicant}
          agreement={agreement}
          job={job}
          onOpenPacketDoc={onOpenPacketDoc}
        />
      )}
    </div>
  );
};

export default ContractorOnboardPanel;
